'use client';

import { getDefaultStore, useStore as useJotaiStore, useSetAtom } from 'jotai';
import { type ReactNode, useEffect, useRef } from 'react';

import {
  chronicleConnectionContextAtom,
  chronicleDeviceRecordingInfoAtom,
  chronicleDeviceStateAtom,
  chronicleInterruptedSessionAtom,
  chronicleLiveOfflineAtom,
  chronicleReconnectedPendingEndAtom,
  chronicleRecordingEndReasonAtom,
} from '@/atoms/chronicle';
import { useIsDesktopChronicleEnabled } from '@/features/flags';
import {
  useChronicleFirmwareCheck,
  useChronicleNotifications,
  useChronicleSessionControl,
  useChronicleStartupAutoConnect,
} from '@/hooks/desktop';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { ChronicleBulkSyncProvider } from '@/providers/chronicle-bulk-sync-provider';
import { store } from '@/store';
import { chronicleBridge } from '@/tauri/chronicle';
import { listenChronicleStateChanged } from '@/tauri/chronicle/events';
import { trackHardwareBleReconnected } from '@/utils/analytics/track-hardware-events';
import { chronicleLogger as logger } from '@/utils/logger/chronicle-logger';

import { useChronicleAudioRmsListener } from './hooks/useChronicleAudioRmsListener';
import { useChronicleAutoSelectInput } from './hooks/useChronicleAutoSelectInput';
import { useChronicleAutoSessionCreatedListener } from './hooks/useChronicleAutoSessionCreatedListener';
import { useChronicleLiveOfflineHoldTimeout } from './hooks/useChronicleLiveOfflineHoldTimeout';
import { useChronicleRecordingRecoveredListener } from './hooks/useChronicleRecordingRecoveredListener';
import { useChronicleRestoreLinkedDevice } from './hooks/useChronicleRestoreLinkedDevice';
import { useChronicleSyncRouteSession } from './hooks/useChronicleSyncRouteSession';
import { useReconcileDeviceFirmware } from './hooks/useReconcileDeviceFirmware';
import { useRemoteSyncAsyncFlagPush } from './hooks/useRemoteSyncAsyncFlagPush';
import { useSetupChronicleOtaEvents } from './hooks/useSetupChronicleOtaEvents';
import { applyDisconnectSideEffects } from './utils/apply-disconnect-side-effects';
import {
  deriveSteFlags,
  extractStopCauses,
  processRecordingDrop,
  recordingInfoFromPayload,
  resolveReconnectPausedInfo,
} from './utils/process-recording-drop';
import { processStateEvent, type StateEventContext } from './utils/process-state-event';
import {
  logRecordingInterruptedHold,
  trackDeviceConnected,
} from './utils/track-connection-events';

interface ChronicleStateProviderProps {
  readonly children: ReactNode;
}

/** Global provider that subscribes to Chronicle device state and audio events.
 * Should be mounted once at the app root level. */
export function ChronicleStateProvider({ children }: ChronicleStateProviderProps) {
  const { isAuthenticated, isLoading: isAuthLoading } = useIsAuthenticated();
  const isChronicleEnabled = useIsDesktopChronicleEnabled();
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const setDeviceRecordingInfo = useSetAtom(chronicleDeviceRecordingInfoAtom);
  const setInterruptedSession = useSetAtom(chronicleInterruptedSessionAtom);
  const setRecordingEndReason = useSetAtom(chronicleRecordingEndReasonAtom);
  const setConnectionContext = useSetAtom(chronicleConnectionContextAtom);
  const setLiveOffline = useSetAtom(chronicleLiveOfflineAtom);

  // APP-4895 (Phase C): read the connection context from the Jotai store
  // directly inside the Rust state-event handler. The previous render-
  // mirrored ref had a race window — between an atom write
  // (`markManualDisconnect`) and the next React render that refreshed the
  // ref, an arriving event would observe stale data and incorrectly treat
  // a manual disconnect as unexpected, kicking auto-reconnect. `store.get`
  // always returns the latest atom value with no rendering involved, so
  // the race surface is closed structurally rather than via tests.
  const jotaiStore = useJotaiStore();

  // Track previous connection status to detect unexpected disconnects
  const prevConnectionStatusRef = useRef<string | null>(null);
  // Track whether device was recording, so we can detect recording→non-recording
  // transitions and set the end reason atomically in the event handler.
  const wasRecordingRef = useRef(false);

  // Auto-select/deselect Chronicle as input source globally so the side nav
  // renders ChronicleBars even before the user visits a session page.
  useChronicleAutoSelectInput();

  // Show notifications for device state changes (e.g., unexpected disconnect toast)
  useChronicleNotifications();

  // Orchestrate device-initiated start/stop/pause/resume flows.
  // Watches chronicleDeviceRecordingInfoAtom transitions and routes them
  // to the unified Chronicle recording hooks.
  useChronicleSessionControl();

  // Sync the current session route into Rust so the auto-session watcher
  // does not race-create an orphan "Untitled" session when the Remote starts
  // recording while a session page is already open.
  useChronicleSyncRouteSession();

  // Restore the linked-device serial on app load so `auto_bulk_sync` fires on
  // any page, not only after the device page (it skips without a serial).
  useChronicleRestoreLinkedDevice();

  // Restore linked device from backend and trigger BLE auto-connect on startup.
  // Transport teardown is settled-logout only — not feature-flag off —
  // because Flagsmith can hydrate after logout and must not drive disconnect.
  const isSettledLogout = !isAuthenticated && !isAuthLoading;
  useChronicleStartupAutoConnect({
    enabled: isAuthenticated && !isAuthLoading && isChronicleEnabled,
    resetOnDisabled: isSettledLogout || !isChronicleEnabled,
    teardownTransportOnSettledLogout: isSettledLogout,
  });

  // Firmware: OTA update events, connect-time backend reconcile (heal a stale
  // record against the live BLE version so a refresh doesn't re-seed the old
  // one — APP-8399), and the Firebase Remote Config update check.
  useSetupChronicleOtaEvents();
  useReconcileDeviceFirmware();
  useChronicleFirmwareCheck();

  const isRemoteSyncAsyncEnabledRef = useRemoteSyncAsyncFlagPush();

  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    const fetchInitialState = async () => {
      try {
        const state = await chronicleBridge.getState();

        // Use functional update so mapDeviceState merges onto whatever
        // useRestoreLinkedDevice may have already written (API can resolve
        // before this Tauri IPC call). For disconnected state this preserves
        // deviceName/serialNumber/firmwareVersion via ...currentState spread;
        // for connected state BLE values take priority via rustState ?? currentState.
        setDeviceState((prev) => {
          const { newDeviceState, newStatus, isSuccessfulConnection } = processStateEvent(
            state,
            prev,
            {
              prevStatus: null,
              lastDisconnectReason: null,
              linkedDeviceId: null,
            }
          );

          prevConnectionStatusRef.current = newStatus;

          if (isSuccessfulConnection) {
            logger.info({
              msg: 'Chronicle device already connected on mount',
              status: newStatus,
            });
          }

          return newDeviceState;
        });

        // Track device recording info when device is already recording on mount
        if (state.type === 'recording') {
          wasRecordingRef.current = true;
          setDeviceRecordingInfo(recordingInfoFromPayload(state));
        }
      } catch (error) {
        logger.warn({
          msg: 'Failed to fetch Chronicle initial state',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void fetchInitialState();
  }, [setDeviceState, setDeviceRecordingInfo]);

  useEffect(() => {
    if (!chronicleBridge.isAvailable()) return;

    let unlistenFn: (() => void) | null = null;
    // APP-7607: guard against the async listen() resolving AFTER this effect
    // has been torn down. The old code assigned `unlistenFn` only after the
    // await, so a fast unmount ran cleanup while it was still null and the
    // resolved Tauri listener leaked — accumulating duplicate listeners across
    // remounts / view churn.
    let cancelled = false;

    const setupListener = async () => {
      try {
        const unlisten = await listenChronicleStateChanged((event) => {
          logger.debug({
            msg: 'Chronicle state_changed event received',
            eventType: event.payload.type,
            payload: event.payload,
          });

          // APP-4895: read fresh from the store, not a render-mirrored ref.
          const currentContext = jotaiStore.get(chronicleConnectionContextAtom);
          const prevStatus = prevConnectionStatusRef.current;
          // The recording-drop handler below clears the ref before the
          // disconnect side-effects read it, so capture it while it still
          // reflects this event.
          const wasRecordingAtEventStart = wasRecordingRef.current;

          const context: StateEventContext = {
            prevStatus,
            lastDisconnectReason: currentContext.lastDisconnectReason,
            linkedDeviceId: currentContext.linkedDeviceId,
          };

          if (event.payload.type === 'recording') {
            setDeviceRecordingInfo(recordingInfoFromPayload(event.payload));
            setRecordingEndReason(null);
            wasRecordingRef.current = true;
          } else {
            const drop = processRecordingDrop(event.payload.type, {
              wasRecording: wasRecordingRef.current,
              isRemoteSyncAsyncEnabled: isRemoteSyncAsyncEnabledRef.current,
              recordingInfo: jotaiStore.get(chronicleDeviceRecordingInfoAtom),
              hasLiveOffline: jotaiStore.get(chronicleLiveOfflineAtom) !== null,
              reconnectedPendingEnd:
                jotaiStore.get(chronicleReconnectedPendingEndAtom) !== null,
              timerSeconds: store.getState().timerModel.seconds,
              anchorMs: Date.now(),
              ...extractStopCauses(event.payload),
              // STE-derived flags: `recordingStateResolved` gates the held-exit,
              // `recordingPreExisting` distinguishes a still-recording broadcast
              // (hold) from a genuine long-press stop (finalize) in the window.
              ...deriveSteFlags(event.payload),
            });
            if (drop.action === 'hold' && drop.liveOffline) {
              // hold + freeze: hold live-offline and stop the
              // timer (iOS parity); recovered event's onStart un-freezes + re-seeds
              // to the device time. Recording-info atoms stay for the reconnect path.
              setLiveOffline(drop.liveOffline);
              logRecordingInterruptedHold(drop.liveOffline);
              store.dispatch.timerModel.onPause();
            } else if (drop.action !== 'hold') {
              // STE&0 exit (APP-7868): release a held live-offline.
              if (drop.clearLiveOffline) setLiveOffline(null);
              if (drop.endReason) setRecordingEndReason(drop.endReason);
              setDeviceRecordingInfo(null);
              wasRecordingRef.current = false;
            }

            // A batlow power-off can trail a drop that already armed the
            // interruption (reconnect-first sequence); that interruption is no
            // longer actionable once the device is off, so un-arm it here —
            // the battery-empty toast is the one surface for this shutdown.
            if (
              event.payload.type === 'disconnected' &&
              event.payload.reason === 'poweroff_batlow'
            ) {
              setInterruptedSession(null);
            }

            // BUG A: a recovered recording pauses/resumes via a
            // `connected` payload (never `recording`); mirror it onto the atom
            // so the pause detector freezes/resumes the timer (no-op when the
            // finalize above nulled the atom — see resolveReconnectPausedInfo).
            const pausedInfo = resolveReconnectPausedInfo(
              event.payload,
              jotaiStore.get(chronicleDeviceRecordingInfoAtom)
            );
            if (pausedInfo) setDeviceRecordingInfo(pausedInfo);
          }

          setDeviceState((prev) => {
            const result = processStateEvent(event.payload, prev, context);

            logger.debug({
              msg: 'Chronicle state transition',
              prevStatus: prev.connectionStatus,
              newStatus: result.newStatus,
              prevDeviceId: prev.deviceId,
              newDeviceId: result.newDeviceState.deviceId,
            });

            prevConnectionStatusRef.current = result.newStatus;

            applyDisconnectSideEffects({
              result,
              linkedDeviceId: currentContext.linkedDeviceId,
              serialNumber: prev.serialNumber,
              prevStatus: prev.connectionStatus,
              wasRecording: wasRecordingAtEventStart,
              setConnectionContext,
            });

            if (result.isSuccessfulConnection && result.bleDeviceId) {
              logger.info({
                msg: 'Chronicle device connected',
                deviceId: currentContext.linkedDeviceId,
                serialNumber: result.newDeviceState.serialNumber,
                firmwareVersion: result.firmwareVersion,
                wasReconnect:
                  prevStatus === 'connecting' || prevStatus === 'reconnecting',
              });
              trackDeviceConnected({
                deviceId: currentContext.linkedDeviceId,
                serialNumber: result.newDeviceState.serialNumber,
                prevStatus,
                firmwareVersion: result.firmwareVersion ?? undefined,
              });
              const interruptedSession = getDefaultStore().get(
                chronicleInterruptedSessionAtom
              );
              if (interruptedSession) {
                trackHardwareBleReconnected(interruptedSession.sessionId);
              }
              if (result.connectionContextUpdate) {
                setConnectionContext((ctx) => ({
                  ...ctx,
                  ...result.connectionContextUpdate,
                }));
              }
            }

            return result.newDeviceState;
          });
        });

        // APP-7607: if the effect was torn down while listen() was in flight,
        // unlisten immediately rather than leaking the resolved listener.
        if (cancelled) {
          unlisten();
          return;
        }
        unlistenFn = unlisten;
      } catch (error) {
        logger.error({
          msg: 'Failed to setup Chronicle state event listener',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    };

    void setupListener();

    return () => {
      cancelled = true;
      unlistenFn?.();
    };
  }, [
    setDeviceState,
    setDeviceRecordingInfo,
    setInterruptedSession,
    setRecordingEndReason,
    setConnectionContext,
    setLiveOffline,
    jotaiStore,
    isRemoteSyncAsyncEnabledRef,
  ]);

  // APP-7868: consume chronicle:recording_recovered — clear the live-offline
  // hold, re-seed the timer, restore the session/recording atoms.
  useChronicleRecordingRecoveredListener(wasRecordingRef);
  // Escape hatch: a hold whose reconnect never arrives finalizes as an
  // interrupted stop after a deadline instead of locking the app.
  useChronicleLiveOfflineHoldTimeout(wasRecordingRef);
  // Consume chronicle:auto_session_created — fire
  // hardware_session_created (live_recording) + register the device filename
  // for device-file→note tracing.
  useChronicleAutoSessionCreatedListener();
  // Subscribe to audio RMS events for real-time visualization.
  useChronicleAudioRmsListener();
  return <ChronicleBulkSyncProvider>{children}</ChronicleBulkSyncProvider>;
}
